<template>
  <div>
    <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
      <div class="md:col-span-6">
        <label class="field-label">University</label>
        <template v-if="allowedUniversities.length > 1">
          <select :value="selectedSlug" class="field-input" @change="onUniversityChange">
            <option
              v-for="university in allowedUniversities"
              :key="university.slug"
              :value="university.slug"
            >
              {{ university.name }} ({{ university.slug }})
            </option>
          </select>
        </template>
        <template v-else>
          <div class="field-readonly">
            {{ allowedUniversities[0]?.name || selectedSlug || 'No university available' }}
            <span v-if="selectedSlug">({{ selectedSlug }})</span>
          </div>
        </template>
      </div>

      <div class="md:col-span-6">
        <label class="field-label">Ceremony</label>
        <select
          :value="selectedCeremonyId"
          class="field-input"
          :disabled="ceremoniesLoading || !ceremonies.length"
          @change="onCeremonyChange"
        >
          <option value="">{{ ceremoniesLoading ? 'Loading ceremonies...' : 'Select ceremony' }}</option>
          <option v-for="ceremony in ceremonies" :key="ceremony.id" :value="String(ceremony.id)">
            {{ formatCeremony(ceremony) }}
          </option>
        </select>
      </div>
    </div>

    <form class="form-action-row" @submit.prevent="submitSearch">
      <div class="query-field-wrap">
        <label class="field-label">Query</label>
        <div ref="queryDropdown" class="query-input-dropdown">
          <input
            :value="queryText"
            type="text"
            class="field-input"
            placeholder="Graduate front flipping on stage"
            @focus="openQueryExamples"
            @click="openQueryExamples"
            @input="onQueryInput"
          />

          <div v-if="showQueryExamples" class="query-examples-dropdown">
            <button
              v-for="example in filteredExampleQueries"
              :key="example"
              type="button"
              class="query-example-option"
              @mousedown.prevent="selectExampleQuery(example)"
            >
              {{ example }}
            </button>

            <div v-if="!filteredExampleQueries.length" class="query-example-empty">
              No matching example queries
            </div>
          </div>
        </div>
      </div>

      <div class="submit-button-wrap">
        <button
          type="submit"
          class="submit-button"
          :disabled="!canSubmitSearch"
        >
          Search
        </button>
      </div>
    </form>

    <p v-if="ceremonyError" class="mt-3 text-sm text-red-600">{{ ceremonyError }}</p>
  </div>
</template>

<script>
const QUERY_LIBRARY = [
  'Graduate dancing on stage',
  'Audience cheering and clapping',
  'Graduate waving to the crowd',
  'Graduate walking across the stage',
  'Graduate shaking hands with the Chancellor',
]

export default {
  name: 'CeremonySearchControls',
  props: {
    allowedUniversities: {
      type: Array,
      default: () => [],
    },
    selectedSlug: {
      type: String,
      default: '',
    },
    ceremonies: {
      type: Array,
      default: () => [],
    },
    ceremoniesLoading: {
      type: Boolean,
      default: false,
    },
    ceremonyError: {
      type: String,
      default: '',
    },
    selectedCeremonyId: {
      type: String,
      default: '',
    },
    queryText: {
      type: String,
      default: '',
    },
    canSubmitSearch: {
      type: Boolean,
      default: false,
    },
    formatCeremony: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showQueryExamples: false,
      exampleQueries: QUERY_LIBRARY,
    }
  },
  computed: {
    filteredExampleQueries() {
      const query = this.queryText.trim().toLowerCase()

      if (!query) {
        return this.exampleQueries
      }

      return this.exampleQueries.filter((example) => example.toLowerCase().includes(query))
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    onUniversityChange(event) {
      this.$emit('update:selectedSlug', event.target.value)
      this.$emit('university-change')
    },
    onCeremonyChange(event) {
      const ceremonyId = event.target.value
      this.$emit('update:selectedCeremonyId', ceremonyId)
      this.$emit('ceremony-change', ceremonyId)
    },
    onQueryInput(event) {
      this.$emit('update:queryText', event.target.value)
      this.showQueryExamples = true
    },
    openQueryExamples() {
      this.showQueryExamples = true
    },
    selectExampleQuery(example) {
      this.$emit('update:queryText', example)
      this.showQueryExamples = false
    },
    handleDocumentClick(event) {
      if (!this.$refs.queryDropdown?.contains(event.target)) {
        this.showQueryExamples = false
      }
    },
    submitSearch() {
      if (!this.canSubmitSearch) return
      this.$emit('submit-search')
    },
  },
}
</script>

<style scoped>
.field-label {
  display: block;
  font-size: 0.875rem;
  color: #4b5563;
  margin-bottom: 0.375rem;
}

.field-input,
.field-readonly {
  width: 100%;
  min-height: 46px;
  border: 1px solid #cfd7e3;
  border-radius: .25rem;
  padding: 0.45rem 0.6rem;
  background: #fff;
  color: #111827;
}

.field-input:disabled {
  background: #f9fafb;
  color: #9ca3af;
}

.field-readonly {
  display: flex;
  align-items: center;
  background: #f8fafc;
}

.form-action-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-top: 16px;
}

.submit-button-wrap {
  width: 180px;
  flex: 0 0 180px;
}

.query-field-wrap {
  flex: 1 1 auto;
}

.query-input-dropdown {
  position: relative;
}

.query-examples-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 20;
  border: 1px solid #cfd7e3;
  border-radius: .25rem;
  background: #fff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.query-example-option {
  display: block;
  width: 100%;
  border: 0;
  padding: 0.7rem 0.75rem;
  background: #fff;
  color: #111827;
  text-align: left;
}

.query-example-option:hover {
  background: #f8fafc;
}

.query-example-empty {
  padding: 0.7rem 0.75rem;
  color: #6b7280;
}

.submit-button {
  width: 100%;
  min-height: 44px;
  border: 1px solid #0f172a;
  border-radius: .25rem;
  padding: 0.45rem 0.6rem;
  background: #0f172a;
  color: #fff;
  font-weight: 600;
  transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, color 0.2s ease;
}

.submit-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

@media (max-width: 767px) {
  .form-action-row {
    flex-direction: column;
    align-items: stretch;
  }

  .submit-button-wrap {
    width: 100%;
    flex-basis: auto;
    order: 2;
  }

  .query-field-wrap {
    width: 100%;
  }
}
</style>
